// ---------------------------------------------------------------------------
// Portfolio Rebalance async-job storage (MULTI-ACCOUNT)
//
// The rebalance endpoint returns an immediate ACK and computes the result
// asynchronously (delivered later via WebSocket or the status poll). Rebalancing
// is now supported for MULTIPLE accounts concurrently, so localStorage holds a
// MAP of jobs keyed by accountId — at most one active job per account (blocking
// is account-level). This module is the single source of truth for that map.
//
// The map survives navigation / reload so results can be surfaced whenever they
// arrive. It is cleared on login and logout (see AuthProvider), so it is
// effectively per-session / per-user.
//
// Shape:
//   { [accountId]: { requestId, accountId, status, seen, createdAt, cacheKey,
//                    snapshot, result } }
// ---------------------------------------------------------------------------

export const PENDING_JOB_KEY = "portfolioRebalancePendingJob";

// A PENDING job older than this is treated as failed because the backend never
// delivered a result. We stop waiting, notify the user to retry, and unblock
// that account. The ceiling is generous because most jobs finish in under a minute.
export const STALE_JOB_MS = 30 * 60 * 1000; // 30 minutes

// The workbench loader waits this long for the async result before handing off
// to the background notification. It is ALSO the "fast result" window: a result
// that arrives within this window is meant to redirect the tab that started it
// (never a notification), so the watcher suppresses the snackbar for any result
// younger than this (see isJobFresh). Shared by the header (loader timeout) and
// the watcher (notification gate) so the two never drift apart.
export const REBALANCE_RESULT_WINDOW_MS = 60 * 1000; // 60 seconds

// Job lifecycle status
export const JOB_STATUS = {
  PENDING: "PENDING", // ACK received, waiting for the async result
  READY: "READY", // result delivered (via WS or poll) and stored
};

// Classified outcome of a delivered RebalanceResponseDTO
export const REBALANCE_OUTCOME = {
  TRADES: "TRADES", // success with at least one trade → Order Preview
  NOT_ACHIEVABLE: "NOT_ACHIEVABLE",
  ALREADY_ACHIEVED: "ALREADY_ACHIEVED",
  NO_TRADES: "NO_TRADES",
  ERROR: "ERROR",
};

// Only TRADES routes the user to the Order Preview screen; every other outcome
// surfaces an info/error modal on the workbench.
export const isPreviewableOutcome = (outcome) =>
  outcome === REBALANCE_OUTCOME.TRADES;

// Normalize an accountId to a stable string map key.
const keyOf = (accountId) => String(accountId);

// ---------------------------------------------------------------------------
// requestId generation — mirrors createMAInventoryTraceId in
// maInventoryMarketDataUtils.js (crypto.randomUUID with a safe fallback).
// ---------------------------------------------------------------------------
export const createRebalanceRequestId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `rebalance-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// ---------------------------------------------------------------------------
// localStorage CRUD (map keyed by accountId)
// ---------------------------------------------------------------------------
export const readAllJobs = () => {
  try {
    const raw = localStorage.getItem(PENDING_JOB_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    // Legacy single-job record (pre multi-account) → ignore; it's cleared on login.
    if (parsed.requestId || parsed.status) return {};
    return parsed;
  } catch (error) {
    console.warn("[rebalance-job] Failed to read jobs", error);
    return {};
  }
};

export const writeAllJobs = (jobs) => {
  try {
    localStorage.setItem(PENDING_JOB_KEY, JSON.stringify(jobs || {}));
  } catch (error) {
    console.warn("[rebalance-job] Failed to persist jobs", error);
  }
  return jobs;
};

export const readJob = (accountId) => {
  if (accountId == null) return null;
  return readAllJobs()[keyOf(accountId)] || null;
};

export const writeJob = (accountId, job) => {
  if (accountId == null) return null;
  const jobs = readAllJobs();
  jobs[keyOf(accountId)] = job;
  writeAllJobs(jobs);
  return job;
};

export const removeJob = (accountId) => {
  if (accountId == null) return;
  const jobs = readAllJobs();
  if (keyOf(accountId) in jobs) {
    delete jobs[keyOf(accountId)];
    writeAllJobs(jobs);
  }
};

// Remove the whole map (login / logout).
export const clearAllJobs = () => {
  try {
    localStorage.removeItem(PENDING_JOB_KEY);
  } catch (error) {
    console.warn("[rebalance-job] Failed to clear jobs", error);
  }
};

// Merge-update one account's job (no-op if none exists).
export const updateJob = (accountId, patch) => {
  const job = readJob(accountId);
  if (!job) return null;
  return writeJob(accountId, { ...job, ...patch });
};

export const markJobSeen = (accountId) => updateJob(accountId, { seen: true });

// ---------------------------------------------------------------------------
// Per-TAB notification marker (sessionStorage, keyed by requestId).
//
// The "Rebalancing complete" toast must show at most once PER TAB and survive a
// reload of that tab (so a dismissed / ignored toast never resurfaces on
// refresh). It must NOT be shared across tabs: each open tab is meant to
// surface the toast once on its own.
//
// This used to be a `notified` flag written onto the shared-localStorage job
// map. Because that map is shared, the first tab to process a result marked it
// notified and every OTHER tab then read `notified:true` and stayed silent —
// even though it should have notified too. sessionStorage has exactly the right
// scope: private to one tab, preserved across that tab's reloads, invisible to
// other tabs. (`seen` — the result was actually opened — stays in the shared
// map: it IS a cross-tab fact and, once set, clears the result everywhere.)
// ---------------------------------------------------------------------------
const TOAST_SHOWN_KEY = "portfolioRebalanceToastShown";

const readToastShownSet = () => {
  try {
    const raw = sessionStorage.getItem(TOAST_SHOWN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("[rebalance-job] Failed to read toast-shown set", error);
    return [];
  }
};

// Has THIS tab already shown the toast for this result (by requestId)?
export const hasToastShownThisTab = (requestId) =>
  !!requestId && readToastShownSet().includes(requestId);

// Record that THIS tab has now shown the toast for this result.
export const markToastShownThisTab = (requestId) => {
  if (!requestId) return;
  const set = readToastShownSet();
  if (set.includes(requestId)) return;
  set.push(requestId);
  try {
    sessionStorage.setItem(TOAST_SHOWN_KEY, JSON.stringify(set));
  } catch (error) {
    console.warn("[rebalance-job] Failed to persist toast-shown set", error);
  }
};

// Clear this tab's toast markers (login / logout, alongside clearAllJobs).
export const clearToastShownThisTab = () => {
  try {
    sessionStorage.removeItem(TOAST_SHOWN_KEY);
  } catch (error) {
    console.warn("[rebalance-job] Failed to clear toast-shown set", error);
  }
};

// Find the account whose job carries this requestId (WS/poll correlation).
export const findJobByRequestId = (requestId) => {
  if (!requestId) return null;
  const jobs = readAllJobs();
  for (const accountId of Object.keys(jobs)) {
    if (jobs[accountId]?.requestId === requestId) {
      return { accountId, job: jobs[accountId] };
    }
  }
  return null;
};

// ---- per-account / aggregate selectors ----

// PENDING for this account (used to block a second concurrent rebalance for it).
export const hasPendingJob = (accountId) =>
  readJob(accountId)?.status === JOB_STATUS.PENDING;

// A delivered result this account hasn't viewed yet.
export const hasReadyUnseenJob = (accountId) => {
  const job = readJob(accountId);
  return !!(job && job.status === JOB_STATUS.READY && !job.seen);
};

// Rebalance stays blocked for an account while it is pending OR its result is
// ready-but-unviewed.
export const hasActiveJob = (accountId) => {
  const job = readJob(accountId);
  if (!job) return false;
  return (
    job.status === JOB_STATUS.PENDING ||
    (job.status === JOB_STATUS.READY && !job.seen)
  );
};

// [{ accountId, job }] for all PENDING jobs (used to poll each).
export const listPendingJobs = () => {
  const jobs = readAllJobs();
  return Object.keys(jobs)
    .filter((a) => jobs[a]?.status === JOB_STATUS.PENDING)
    .map((a) => ({ accountId: a, job: jobs[a] }));
};

// [{ accountId, job }] for all READY & unseen results (used to decide preview
// vs workbench, and to drive the nav dot).
export const listReadyUnseenJobs = () => {
  const jobs = readAllJobs();
  return Object.keys(jobs)
    .filter((a) => jobs[a]?.status === JOB_STATUS.READY && !jobs[a]?.seen)
    .map((a) => ({ accountId: a, job: jobs[a] }));
};

// Any account with an unseen ready result (drives the nav green dot).
export const anyReadyUnseen = () => listReadyUnseenJobs().length > 0;

// A PENDING job that has been waiting past STALE_JOB_MS — the result almost
// certainly failed / was lost. Callers expire it (notify + unblock).
export const isJobStale = (job) =>
  !!(
    job &&
    job.status === JOB_STATUS.PENDING &&
    job.createdAt &&
    Date.now() - job.createdAt > STALE_JOB_MS
  );

// A job whose result arrived within the "fast" window (see REBALANCE_RESULT_WINDOW_MS).
// A fresh result is one the initiating tab's loader is meant to redirect on, so the
// watcher never raises a notification for it — even when no live loader exists in
// this tab (e.g. after a reload, or when a different tab is the initiator). This is
// how "never notify when results arrive under a minute" is guaranteed everywhere.
export const isJobFresh = (job) =>
  !!(job && job.createdAt && Date.now() - job.createdAt < REBALANCE_RESULT_WINDOW_MS);

// ---------------------------------------------------------------------------
// Result classification — mirrors the branching that used to live inline in
// portfolioRebalanceStore.rebalance() when the response was synchronous.
// ---------------------------------------------------------------------------
export const classifyRebalanceResult = (dto) => {
  if (!dto || dto.status === 400) {
    return {
      outcome: REBALANCE_OUTCOME.ERROR,
      message: dto?.message || "Failed to calculate rebalance",
    };
  }

  if (!dto.success) {
    return {
      outcome: REBALANCE_OUTCOME.ERROR,
      message: dto.message || "Failed to calculate rebalance",
    };
  }

  if (dto.isAchievable === false) {
    return {
      outcome: REBALANCE_OUTCOME.NOT_ACHIEVABLE,
      message: dto.message || "Target not achievable",
    };
  }

  if (dto.isTargetAlreadyAchieved) {
    return {
      outcome: REBALANCE_OUTCOME.ALREADY_ACHIEVED,
      message:
        dto.message ||
        "The current value is already within the defined target range.",
    };
  }

  if (Array.isArray(dto.trades) && dto.trades.length === 0) {
    return {
      outcome: REBALANCE_OUTCOME.NO_TRADES,
      message: "No trades could be generated using the provided parameters.",
    };
  }

  return { outcome: REBALANCE_OUTCOME.TRADES, message: dto.message || "" };
};

// The status poll can return a still-processing acknowledgment (status
// PENDING) before the async computation finishes. Only treat a payload as a
// real result once it is terminal. WebSocket payloads are always terminal,
// so this gate is effectively a no-op there.
export const isTerminalRebalanceResult = (dto) => {
  if (!dto) return false;

  // Strong signals of a computed result → terminal regardless of the status
  // field. A pending acknowledgment does not carry these fields, so it is
  // correctly treated as not-ready below.
  if (
    Array.isArray(dto.trades) ||
    dto.isAchievable != null ||
    dto.isTargetAlreadyAchieved != null ||
    dto.success === false
  ) {
    return true;
  }

  // No result signals — fall back to the status field. Anything pending-like is
  // still processing; a non-empty non-pending status is treated as terminal.
  const status = String(dto.status || "").trim().toUpperCase();
  if (
    status === JOB_STATUS.PENDING ||
    status === "PROCESSING" ||
    status === "IN_PROGRESS" ||
    status === "QUEUED"
  ) {
    return false;
  }
  return status !== "";
};

// ---------------------------------------------------------------------------
// Result-waiter registry (in-memory), keyed by requestId.
//
// The workbench header shows its "Rebalancing..." loader for up to 60s after
// the ACK. During that window it awaits the result here; the watcher resolves
// the waiter as soon as the result is processed. The registry is intentionally
// in-memory only — after a reload there is no waiter, so the result flows
// through the notification (dot + snackbar) path instead.
// ---------------------------------------------------------------------------
const resultWaiters = new Map();

// Resolves with { timedOut: true } on timeout, or { timedOut: false, result }
// when the watcher delivers the processed result.
export const waitForRebalanceResult = (requestId, timeoutMs) =>
  new Promise((resolve) => {
    const entry = {};
    entry.timer = setTimeout(() => {
      resultWaiters.delete(requestId);
      resolve({ timedOut: true });
    }, timeoutMs);
    entry.resolve = (result) => {
      clearTimeout(entry.timer);
      resultWaiters.delete(requestId);
      resolve({ timedOut: false, result });
    };
    resultWaiters.set(requestId, entry);
  });

// Returns true when a live waiter existed and consumed the result (i.e. the
// header is actively showing it → the watcher should NOT raise a notification).
export const resolveRebalanceResultWaiter = (requestId, result) => {
  const entry = resultWaiters.get(requestId);
  if (!entry) return false;
  entry.resolve(result);
  return true;
};

export const clearRebalanceResultWaiter = (requestId) => {
  const entry = resultWaiters.get(requestId);
  if (entry?.timer) clearTimeout(entry.timer);
  resultWaiters.delete(requestId);
};
